import React from 'react';

/**
 * Error Boundary that catches rendering errors.
 * Handles DOM manipulation errors caused by browser extensions
 * (Google Translate, password managers, Grammarly, etc.)
 * that modify the DOM and conflict with React's virtual DOM reconciliation.
 * 
 * Recovery strategy:
 * 1. DOM errors (extensions) → silent recovery, re-render
 * 2. Other errors → show retry UI, don't force full reload
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error) {
    const msg = error?.message || '';
    const isDOMError =
      msg.includes('removeChild') ||
      msg.includes('insertBefore') ||
      msg.includes('appendChild') ||
      msg.includes('not a child') ||
      msg.includes("n'est pas un enfant") ||
      msg.includes('Node') ||
      msg.includes('Failed to execute') ||
      msg.includes('Cannot read properties of null');

    if (isDOMError) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const msg = error?.message || '';
    const isDOMError =
      msg.includes('removeChild') ||
      msg.includes('insertBefore') ||
      msg.includes('appendChild') ||
      msg.includes('not a child') ||
      msg.includes("n'est pas un enfant") ||
      msg.includes('Node') ||
      msg.includes('Failed to execute') ||
      msg.includes('Cannot read properties of null');

    if (isDOMError) {
      this.setState({ hasError: false });
      return;
    }

    this.setState(prev => ({ errorCount: prev.errorCount + 1 }));
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorCount: 0 });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          background: '#0a0a0a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          fontFamily: 'system-ui, sans-serif'
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <div style={{ marginBottom: 16 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#7CFC00" strokeWidth="2" style={{ margin: '0 auto', display: 'block' }}>
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 8 }}>
              Oups, un petit souci !
            </h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
              {this.state.errorCount > 1
                ? "Si le probleme persiste, essayez de desactiver Google Traduction ou utilisez la navigation privee."
                : "Un probleme d'affichage est survenu. Cliquez ci-dessous pour reessayer."
              }
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={this.handleRetry}
                style={{
                  background: '#7CFC00',
                  color: '#000',
                  border: 'none',
                  padding: '12px 32px',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Reessayer
              </button>
              {this.state.errorCount > 1 && (
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: 'transparent',
                    color: '#888',
                    border: '1px solid #333',
                    padding: '10px 32px',
                    borderRadius: 12,
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  Recharger la page
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
